import type { ReactNode } from "react";

import { AlertIcon, CheckCircleIcon, InboxIcon, InfoIcon } from "./icons";

/* ==========================================================================
   Empty, error, loading and notice states.

   One shape for each, because "the moment a new user first arrives" was the
   least consistent screen on every site: three headings levels, three CTA
   weights and three surfaces for the same absence.
   ========================================================================== */

export type HeadingLevel = 2 | 3 | 4;

export interface StateAction {
  label: string;
  /** Rendered by the caller's router if given, otherwise as a button. */
  onClick?: () => void;
  href?: string;
  /**
   * Empty-state actions are SECONDARY by default. An orange gradient here is
   * how a screen ends up with two primaries doing the same thing — the page
   * head's "Add service" and the empty state's "Add your first service".
   * Opt in only when the screen has no other primary.
   */
  primary?: boolean;
}

function actionClass(action: StateAction): string {
  return `btn ${action.primary ? "btn-primary" : "btn-secondary"}`;
}

export function EmptyState({
  title,
  children,
  icon,
  action,
  headingLevel = 2,
  renderLink,
}: {
  title: string;
  children?: ReactNode;
  icon?: ReactNode;
  action?: StateAction | ReactNode;
  /** A full-screen empty state is a real heading; AT users must be able to
   *  jump to it. `<p class="state__title">` was the old shape and gave them
   *  nothing. */
  headingLevel?: HeadingLevel;
  /** Sites with a router pass their Link so an `href` action client-routes. */
  renderLink?: (props: { href: string; className: string; children: ReactNode }) => ReactNode;
}) {
  const Heading = `h${headingLevel}` as const;

  return (
    <div className="empty-state">
      {icon ?? <InboxIcon />}
      <Heading className="empty-state__title">{title}</Heading>
      {children ? <p className="lead">{children}</p> : null}
      {renderStateAction(action, renderLink)}
    </div>
  );
}

function renderStateAction(
  action: StateAction | ReactNode | undefined,
  renderLink?: (props: { href: string; className: string; children: ReactNode }) => ReactNode,
): ReactNode {
  if (!action) return null;
  if (typeof action !== "object" || !("label" in (action as StateAction))) {
    return action as ReactNode;
  }
  const spec = action as StateAction;
  const className = actionClass(spec);
  if (spec.href) {
    if (renderLink) return renderLink({ href: spec.href, className, children: spec.label });
    return (
      <a className={className} href={spec.href}>
        {spec.label}
      </a>
    );
  }
  return (
    <button type="button" className={className} onClick={spec.onClick}>
      {spec.label}
    </button>
  );
}

export function ErrorState({
  message,
  title = "We couldn't load this",
  onRetry,
  headingLevel = 2,
}: {
  /** Already turned into copy a person can act on by the caller. */
  message: string;
  title?: string;
  onRetry?: () => void;
  headingLevel?: HeadingLevel;
}) {
  const Heading = `h${headingLevel}` as const;

  return (
    <div className="empty-state">
      <AlertIcon />
      <Heading className="empty-state__title">{title}</Heading>
      <p className="lead">{message}</p>
      {onRetry ? (
        <button type="button" className="btn btn-secondary" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function Skeleton({ height = "5rem" }: { height?: string | undefined }) {
  return <div className="skeleton" style={{ height }} aria-hidden="true" />;
}

export function SkeletonList({
  rows = 3,
  height,
}: {
  rows?: number | undefined;
  height?: string | undefined;
}) {
  return (
    <div className="stack" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} {...(height ? { height } : {})} />
      ))}
    </div>
  );
}

export function Spinner({ size = "md", label }: { size?: "md" | "lg"; label?: string }) {
  return (
    <>
      <span className={size === "lg" ? "spinner spinner--lg" : "spinner"} aria-hidden="true" />
      {label ? <span className="sr-only">{label}</span> : null}
    </>
  );
}

/** A whole-screen wait: session gates, mode gates, first paint. */
export function FullPageSpinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="spinner--page" role="status">
      <span className="spinner spinner--lg" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

export type BannerTone = "info" | "warn" | "danger" | "success";

const BANNER_GLYPH = {
  info: InfoIcon,
  warn: AlertIcon,
  danger: AlertIcon,
  success: CheckCircleIcon,
} as const;

/**
 * A persistent notice.
 *
 * One NEUTRAL surface for every tone; the tone is carried by the icon and the
 * title, never by a tinted box — the same rule that keeps a field error from
 * turning the whole input red.
 */
export function Banner({
  tone = "info",
  title,
  children,
  action,
  role = "status",
}: {
  tone?: BannerTone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
  /** `alert` interrupts whatever the reader is saying — only for something
   *  that appeared in response to what the person just did. */
  role?: "status" | "alert";
}) {
  const Glyph = BANNER_GLYPH[tone];

  return (
    <div className={`banner banner--${tone}`} role={role}>
      <Glyph />
      <div className="stack stack--tight">
        {title ? <span className="banner__title">{title}</span> : null}
        {children ? <span className="muted">{children}</span> : null}
      </div>
      {action ? <div className="banner__action">{action}</div> : null}
    </div>
  );
}
